import React, { useMemo, useState } from 'react'
import {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	SortingState,
	Column,
} from '@tanstack/react-table'
import {
	EditOutlined,
	Delete,
	Groups,
	Person,
	LocalOfferRounded,
	FormatListBulletedOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	UnfoldMore,
} from '@mui/icons-material'
import { IconButton, Checkbox, Tooltip } from '../../components/ui'
import { useUserDateFormat } from '../useUserDateFormat'
import cn from '../../lib/utils/cn'

export type AdminItemType = 'group' | 'user' | 'role' | 'permission'

export const getItemIcon = (type: AdminItemType, isChecked?: boolean) => {
	const iconClass = isChecked ? 'fill-accent' : 'fill-text-secondary'

	switch (type) {
		case 'group':
			return <Groups className={iconClass} />
		case 'user':
			return <Person className={iconClass} />
		case 'role':
			return <LocalOfferRounded className={iconClass} />
		case 'permission':
			return <FormatListBulletedOutlined className={iconClass} />
		default:
			return null
	}
}

export interface AdminItem {
	id: string
	name: string
	modifiedAt?: Date | string
	parentId?: string | null
	type?: AdminItemType
	[key: string]: any
}

export type AdminDefaultDataTableEntry = AdminItem & {
	options: {
		id: string
	}
}

interface useCreateAdminDefaultDateTableProps<T extends AdminItem> {
	data?: T[] | null
	searchQuery?: string
	limit?: number
	offset?: number
	isMobile?: boolean
	checkedItems?: string[]
	onEdit?: (item: T) => void
	onEditItem?: (item: T) => boolean
	onDelete?: (item: T) => void
	onCheckItem?: (itemId: string, checked: boolean) => void
}

export const useCreateAdminDefaultDateTable = <T extends AdminItem>({
	data = [],
	searchQuery = '',
	limit = 10,
	offset = 0,
	isMobile,
	checkedItems = [],
	onEdit,
	onEditItem,
	onDelete,
	onCheckItem,
}: useCreateAdminDefaultDateTableProps<T>) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const [sorting, setSorting] = useState<SortingState>([])

	const tableData: AdminDefaultDataTableEntry[] = useMemo(() => {
		return (data ?? []).map((item) => ({
			...item,
			options: {
				id: item.id,
			},
		}))
	}, [data])

	const renderHeaderItem = (
		title: string,
		column: Column<AdminDefaultDataTableEntry, unknown>,
		className?: string
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 dark:text-text-table-dark',
					className
				)}
			>
				<span className="text-center w-fit">{title}</span>
				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column.getIsSorted() === false && 'hidden group-hover:inline-block'
					)}
				>
					{column.getIsSorted() === 'asc' && (
						<KeyboardArrowUp className="w-5 h-5" />
					)}
					{column.getIsSorted() === 'desc' && (
						<KeyboardArrowDown className="w-5 h-5" />
					)}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}

	const columns = useMemo<ColumnDef<any>[]>(() => {
		const base: ColumnDef<any>[] = [
			{
				accessorKey: 'name',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: 140,
				maxSize: 500,
				size: isMobile ? 200 : 300,
				header: ({ column }) => renderHeaderItem('Name', column),
				cell: ({ getValue, row }) => {
					const isChecked = checkedItems.includes(row.original.id)
					const icon = row.original.type
						? getItemIcon(row.original.type, isChecked)
						: null

					return (
						<div className="flex items-center gap-2 lg:gap-3">
							{onCheckItem && (
								<Checkbox
									checked={isChecked}
									onChange={(checked) => onCheckItem(row.original.id, checked)}
								/>
							)}
							{icon}
							<span className="dark:text-text-table-dark">
								{String(getValue() ?? '')}
							</span>
						</div>
					)
				},
			},
			{
				accessorKey: 'modifiedAt',
				enableSorting: true,
				enableResizing: !isMobile,
				minSize: isMobile ? 50 : 100,
				maxSize: 300,
				size: isMobile ? 50 : 100,
				header: ({ column }) => renderHeaderItem('Modified Date', column),
				cell: ({ getValue }) => {
					const value = getValue() as Date | string | undefined
					return (
						value ? (
							<Tooltip label={formatDateTime(String(value))} classNameChildrenWrapper="w-fit">
								<p className="text-start dark:text-text-table-dark">
									{formatDate(String(value))}
								</p>
							</Tooltip>
						) : (
							<p className="text-start dark:text-text-table-dark">-</p>
						)
					)
				},
			},
			{
				accessorKey: 'options',
				enableSorting: false,
				enableResizing: false,
				size: isMobile ? 80 : 150,
				header: '',
				cell: ({ row }) => {
					const item = row.original as unknown as T
					const showEdit = !!onEdit && (onEditItem ? onEditItem(item) : true)
					return (
						<div className="flex items-center justify-end gap-2 md:gap-3 lg:gap-3 p-2 lg:opacity-0 opacity-100 group-hover:opacity-100 duration-default">
							{showEdit && (
								<IconButton
									variant="secondary"
									icon={
										<EditOutlined className="fill-text-main dark:fill-text-main-dark" />
									}
									onClick={() => onEdit?.(item)}
									tooltip="Edit"
									className="w-8 h-8 dark:text-text-main-dark"
								/>
							)}
							{!!onDelete && (
								<IconButton
									variant="secondary"
									icon={
										<Delete className="fill-text-main dark:fill-text-main-dark" />
									}
									onClick={() => onDelete(row.original as unknown as T)}
									tooltip="Delete"
									className="w-8 h-8 dark:text-text-main-dark"
								/>
							)}
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter((columnDef) => {
				const accessorKey = (columnDef as any)?.accessorKey as string
				return accessorKey && ['id', 'name', 'options'].includes(accessorKey)
			})
		}

		return base
	}, [onEdit, onEditItem, onDelete, onCheckItem, checkedItems, renderHeaderItem, isMobile])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		enableSorting: true,
		enableSortingRemoval: true,
		onSortingChange: setSorting,
		state: {
			sorting,
			globalFilter: searchQuery,
			pagination: {
				pageSize: limit,
				pageIndex: offset / limit,
			},
		},
		globalFilterFn: (row, columnId, filterValue) => {
			const searchValue = String(filterValue).toLowerCase()
			const name = String(row.original.name ?? '').toLowerCase()
			return name.includes(searchValue)
		},
	})

	return {
		table,
	}
}
